import { useQuery } from '@apollo/client'
import { FormattedNumber } from '@faceup/localization'
import { Link, useParams } from '@faceup/router'
import { Card, ContentLayout, PageContentSkeleton, usePromiseSuspense } from '@faceup/ui'
import { Alert, Button, Descriptions, Space, Tag } from '@faceup/ui-base'
import {
  convertCountryToCountryName,
  convertRegionToRegionName,
  DEFAULT_COUNTRY,
  Institution,
  Plan,
  planMapName,
  type SupportedRegion,
} from '@faceup/utils'
import moment from 'moment-timezone'
import { Suspense } from 'react'
import { graphql } from '../__generated__'
import { ForceDeleteInstitutionButton } from '../Components/ForceDeleteInstitution'
import { IntegrationsCard } from '../Components/IntegrationsCard'
import { LinkCompanyToPartner } from '../Components/LinkCompanyToPartner'
import { MembersTable } from '../Components/MembersTable'
import { PaymentLink } from '../Components/PaymentLink'
import { RegistrationButtons } from '../Components/RegistrationButtons'
import { RemoveSsoButton } from '../Components/RemoveSsoButton'
import { ReportSourcesCard } from '../Components/ReportSourcesCard'
import { ResetDeleteReportsIntervalButton } from '../Components/ResetDeleteReportsIntervalButton'
import { RequestOpenIdConfigurationChangeButton } from '../Components/SendOpenIdUpdateEmailButton'
import { UnlinkCompanyFromPartner } from '../Components/UnlinkCompanyFromPartner'
import { useAdminAccessRights } from '../Contexts/AdminAccessRightsProvider'
import { CustomApolloProvider } from '../CustomApolloProvider'
import { DATE_FORMAT } from '../constants'
import { RouteNotFound } from '../RouteNotFound'
import { TrpcProvider } from '../TrpcProvider'
import { DEFAULT_SUPPORTED_REGION, useRegion } from '../utils/useRegion'

export const query = {
  InstitutionQuery: graphql(`
    query InstitutionQuery($institutionId: UUID!, $motherId: UUID!) {
      institution(organizationalUnitId: $institutionId) {
        id
        type
        identificationNumber
        country
        registered
        pdfPassword(motherId: $motherId)

        config {
          id
          institutionName
          street
          city
          zipCode
          state
        }

        billing {
          id
          subscriptionStart
          subscriptionEnd
          subscriptionStatus
          invoicedTo
          plan
          customerUrl
          hotlineCredits
          currency
        }

        partner {
          id
          name
        }

        realInstitution
        isVerified
        createdAt
        isE2EE

        isPaylocityEnabled
        enableDeleteReports
        deleteReportsMonthInterval
        openIdProviderUrl
      }
    }
  `),
}

export const InstitutionDetailView = () => {
  const { id } = useParams<'id'>()

  if (!id) {
    return <RouteNotFound />
  }

  return (
    <Suspense fallback={<PageContentSkeleton />}>
      <InstitutionDetailWithRegion institutionId={id} />
    </Suspense>
  )
}

type InstitutionDetailWithRegionProps = {
  institutionId: string
}

const InstitutionDetailWithRegion = ({ institutionId }: InstitutionDetailWithRegionProps) => {
  const { region, setRegion, discoverByInstitution } = useRegion()
  // Effective region the administration targets - the API falls back to the default region when none
  // is selected, so mirror that here.
  const currentRegion = region ?? DEFAULT_SUPPORTED_REGION

  // The institution may live in a different region than the one the administration is currently
  // opened in (e.g. when arriving via a cross-region deep link). Discover its region first - this
  // is region-agnostic.
  const institutionRegion = usePromiseSuspense(discoverByInstitution, [institutionId])

  // tRPC (the last-investigator pre-check) must hit the same region as Apollo, so the provider is
  // mounted here where the institution's region is known — not at the app root, whose region is the
  // admin's current selection and would be wrong for a cross-region institution.
  const effectiveRegion = institutionRegion ?? currentRegion

  // Same region (or unknown) - render the detail normally on the current client.
  if (!institutionRegion || institutionRegion === currentRegion) {
    return (
      <TrpcProvider region={effectiveRegion}>
        <InstitutionDetailContent institutionId={institutionId} />
      </TrpcProvider>
    )
  }

  // Different region - pin the detail to the institution's region via its own Apollo client so every
  // query and action targets the right region (regions share credentials), and surface a banner
  // offering to switch the administration region.
  return (
    <CustomApolloProvider region={institutionRegion}>
      <TrpcProvider region={institutionRegion}>
        <InstitutionDetailContent
          institutionId={institutionId}
          regionMismatch={{
            currentRegion,
            institutionRegion,
            onSwitch: () => setRegion(institutionRegion),
          }}
        />
      </TrpcProvider>
    </CustomApolloProvider>
  )
}

type RegionMismatch = {
  currentRegion: SupportedRegion
  institutionRegion: SupportedRegion
  onSwitch: () => void
}

type InstitutionDetailContentProps = {
  institutionId: string
  regionMismatch?: RegionMismatch
}

const InstitutionDetailContent = ({
  institutionId,
  regionMismatch,
}: InstitutionDetailContentProps) => {
  const {
    isAllowedInstitutionEdit,
    isAllowedInstitutionLink,
    isAllowedCreatePaymentUrl,
    isAllowedApproveOrRejectSchool,
  } = useAdminAccessRights()

  const { loading, data } = useQuery(query.InstitutionQuery, {
    fetchPolicy: 'cache-and-network',
    variables: { motherId: institutionId, institutionId },
  })

  if (loading && !data) {
    return <PageContentSkeleton />
  }

  const institution = data?.institution

  if (institution?.__typename !== 'Company') {
    return <RouteNotFound />
  }

  return (
    <ContentLayout header={<ContentLayout.Header title={institution.config.institutionName} />}>
      <Space direction='vertical' size='large' style={{ width: '100%' }}>
        {regionMismatch && <RegionMismatchAlert {...regionMismatch} />}
        <Card>
          <Card.Header>Basic info</Card.Header>
          <Card.Content>
            <Descriptions style={{ marginTop: 20 }}>
              <Descriptions.Item label='Type'>{institution?.type}</Descriptions.Item>
              <Descriptions.Item label='Identification number'>
                {institution?.identificationNumber}
              </Descriptions.Item>
              <Descriptions.Item label='Country'>
                {convertCountryToCountryName(institution?.country ?? DEFAULT_COUNTRY)}
              </Descriptions.Item>
              <Descriptions.Item label='Plan'>
                {planMapName[institution?.billing?.plan ?? Plan.Unregistered]}
              </Descriptions.Item>
              <Descriptions.Item label='Street'>{institution?.config.street}</Descriptions.Item>
              <Descriptions.Item label='City'>{institution?.config.city}</Descriptions.Item>
              <Descriptions.Item label='ZipCode'>{institution?.config.zipCode}</Descriptions.Item>
              <Descriptions.Item label='State'>{institution?.config.state}</Descriptions.Item>
              <Descriptions.Item label='Registered'>
                {institution?.createdAt && moment(institution?.createdAt).format(DATE_FORMAT)}
              </Descriptions.Item>
              <Descriptions.Item label='Subscription start'>
                {institution?.billing?.subscriptionStart &&
                  moment(institution.billing.subscriptionStart).format(DATE_FORMAT)}
              </Descriptions.Item>
              <Descriptions.Item label='Subscription end'>
                {institution?.billing?.subscriptionEnd &&
                  moment(institution.billing.subscriptionEnd).format(DATE_FORMAT)}
              </Descriptions.Item>
              <Descriptions.Item label='Institution'>
                {institution?.realInstitution ? 'Real' : 'Demo'}
              </Descriptions.Item>
              <Descriptions.Item label='End-to-end encryption'>
                <Tag color={institution.isE2EE ? 'orange' : 'default'}>
                  {institution.isE2EE ? 'On' : 'Off'}
                </Tag>
              </Descriptions.Item>
              {institution?.type === Institution.School && (
                <Descriptions.Item label='PDF password'>
                  {institution?.pdfPassword}
                </Descriptions.Item>
              )}
              {institution.partner && (
                <Descriptions.Item label='Partner'>
                  <Link to={routes => routes.partner({ id: institution.partner?.id ?? '' })}>
                    {institution.partner.name}
                  </Link>
                </Descriptions.Item>
              )}
              <Descriptions.Item label='Current Subscription status'>
                {institution?.billing?.subscriptionStatus}
              </Descriptions.Item>
              {institution?.billing?.customerUrl && (
                <Descriptions.Item label='Subscription info'>
                  <a
                    target='_blank'
                    href={institution.billing.customerUrl}
                    rel='noreferrer noopener'
                  >
                    Chargebee
                  </a>
                </Descriptions.Item>
              )}
              {institution?.partner?.id && (
                <Descriptions.Item label='Invoiced to'>
                  {institution?.billing?.invoicedTo}
                </Descriptions.Item>
              )}
              {typeof institution?.billing?.hotlineCredits === 'number' && (
                <Descriptions.Item label='Automated/AI hotline credits'>
                  <FormattedNumber
                    style='currency'
                    value={institution.billing.hotlineCredits / 100}
                    currency={institution.billing.currency ?? undefined}
                    maximumFractionDigits={2}
                  />
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card.Content>
        </Card>
        <Card>
          <Card.Header>Actions</Card.Header>
          <Card.Content>
            <Space style={{ flexWrap: 'wrap' }}>
              {isAllowedCreatePaymentUrl &&
                institution?.registered &&
                institution?.billing?.customerUrl && (
                  <>
                    <PaymentLink motherId={institution?.id} />
                    <PaymentLink motherId={institution?.id} isOfflinePayment />
                  </>
                )}
              {isAllowedApproveOrRejectSchool &&
                !institution?.isVerified &&
                institution.billing?.plan !== Plan.Unregistered && (
                  <RegistrationButtons
                    institutionId={institution?.id ?? null}
                    type={institution?.type}
                  />
                )}
              {isAllowedInstitutionLink &&
                (institution?.partner?.id ? (
                  <UnlinkCompanyFromPartner
                    partnerId={institution?.partner?.id ?? ''}
                    motherId={institution?.id ?? ''}
                  />
                ) : institution?.registered ? (
                  <LinkCompanyToPartner
                    partnerId={institution?.partner?.id}
                    motherId={institution?.id ?? ''}
                  />
                ) : null)}
              <ForceDeleteInstitutionButton
                institutionId={institution?.id ?? null}
                type={institution?.type}
                realInstitution={institution?.realInstitution}
                disabled={!isAllowedInstitutionEdit}
              />
              {isAllowedInstitutionEdit && (
                <ResetDeleteReportsIntervalButton
                  motherId={institution?.id ?? ''}
                  isAlreadySet={
                    (institution?.enableDeleteReports &&
                      institution?.deleteReportsMonthInterval === 0) ??
                    false
                  }
                />
              )}
              {isAllowedInstitutionEdit && institution.openIdProviderUrl && (
                <>
                  <RequestOpenIdConfigurationChangeButton motherId={institution.id} />
                  <RemoveSsoButton motherId={institution.id} />
                </>
              )}
            </Space>
          </Card.Content>
        </Card>
        <ReportSourcesCard institutionId={institution?.id} />
        <IntegrationsCard institution={institution} />
        <MembersTable institutionId={institution?.id} />
      </Space>
    </ContentLayout>
  )
}

const RegionMismatchAlert = ({ currentRegion, institutionRegion, onSwitch }: RegionMismatch) => (
  <Alert
    type='warning'
    message='This institution is in a different region'
    description={
      <>
        It exists in the <strong>{convertRegionToRegionName(institutionRegion)}</strong> region, but
        your administration is opened in the{' '}
        <strong>{convertRegionToRegionName(currentRegion)}</strong> region. All actions are still
        available here.
      </>
    }
    action={
      <Button size='small' type='primary' onClick={onSwitch}>
        Switch to {convertRegionToRegionName(institutionRegion)}
      </Button>
    }
  />
)
